import { useCallback, useEffect, useState } from 'react';
import { Icon, tokens } from '@/design';
import { auditClient, type AuditClientState, type AuditFilters, type AuditRow } from '@/app/audit/auditDataClient';

export interface AuditDrawerProps {
  sessionId?: string;
  focusRowId?: string;
  onRowClick?: (rowId: string) => void;
  onEvidencePackRequested?: (selectedRows: string[]) => void;
  onFullAuditRequested?: (filters: AuditFilters) => void;
}

export function AuditDrawer({
  sessionId,
  focusRowId,
  onRowClick,
  onEvidencePackRequested,
  onFullAuditRequested,
}: AuditDrawerProps) {
  const [state, setState] = useState<AuditClientState>({ kind: 'loading' });
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const loadRows = useCallback(async () => {
    setState({ kind: 'loading' });
    setState(await auditClient.listAuditRows(sessionId));
  }, [sessionId]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const rows = state.kind === 'success' ? state.data.rows : [];

  return (
    <section
      role="complementary"
      aria-label="Session audit"
      data-gvw-id="GVW-130"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <header style={{ padding: tokens.space[3], borderBottom: tokens.border.thin }}>
        <h2 style={{ margin: 0, fontSize: tokens.font.size.lg }}>
          <Icon name="policy" size={20} decorative /> Session Audit
        </h2>
        <p style={{ margin: `${tokens.space[1]}px 0 0`, color: tokens.color.ink.secondary }}>
          Review session events and export evidence packs.
        </p>
      </header>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {state.kind === 'loading' ? <Status message="Loading audit rows..." /> : null}
        {state.kind === 'backend-not-deployed' ? (
          <Status message="Audit backend is not deployed yet." icon="construction" />
        ) : null}
        {state.kind === 'error' ? <Status message={state.message} icon="error" /> : null}
        {state.kind === 'success' && rows.length === 0 ? <Status message="No audit entries yet." icon="history" /> : null}
        {state.kind === 'success' && rows.length > 0 ? (
          <ul role="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {rows.map((row, index) => (
              <AuditRowItem
                key={row.id}
                row={row}
                index={index}
                focused={row.id === focusRowId}
                selected={selectedRows.has(row.id)}
                onSelect={(selected) =>
                  setSelectedRows((current) => {
                    const next = new Set(current);
                    if (selected) next.add(row.id);
                    else next.delete(row.id);
                    return next;
                  })
                }
                onOpen={() => onRowClick?.(row.id)}
              />
            ))}
          </ul>
        ) : null}
      </div>

      <footer
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: tokens.space[2],
          padding: tokens.space[3],
          borderTop: tokens.border.thin,
        }}
      >
        <button type="button" onClick={() => onFullAuditRequested?.({})} style={buttonStyle('secondary')}>
          <Icon name="open_in_new" size={16} decorative /> Open full audit
        </button>
        <button
          type="button"
          disabled={selectedRows.size === 0}
          onClick={() => onEvidencePackRequested?.([...selectedRows])}
          style={buttonStyle(selectedRows.size === 0 ? 'disabled' : 'primary')}
        >
          <Icon name="download" size={16} decorative /> Export selected
        </button>
      </footer>
    </section>
  );
}

function AuditRowItem({
  row,
  index,
  selected,
  focused,
  onSelect,
  onOpen,
}: {
  row: AuditRow;
  index: number;
  selected: boolean;
  focused: boolean;
  onSelect: (selected: boolean) => void;
  onOpen: () => void;
}) {
  return (
    <li
      tabIndex={0}
      aria-label={`Audit row: ${row.action} on ${row.target} by ${row.actor}`}
      style={{
        padding: tokens.space[2],
        borderBottom: tokens.border.thin,
        background: focused ? tokens.color.surface[2] : 'transparent',
      }}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
        <input
          type="checkbox"
          checked={selected}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => onSelect(event.currentTarget.checked)}
          aria-label={`Select audit row ${index + 1}`}
          style={{ margin: 0 }}
        />
        <Icon name={row.outcome === 'success' ? 'check_circle' : row.outcome === 'blocked' ? 'block' : row.outcome === 'pending' ? 'pending' : 'error'} size={20} decorative />
        <span style={{ minWidth: 0 }}>
          <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.action}
          </strong>
          <span style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>
            {row.target} • {new Date(row.createdAt).toLocaleTimeString()}
          </span>
        </span>
      </div>
    </li>
  );
}

function Status({ message, icon = 'pending' }: { message: string; icon?: 'pending' | 'construction' | 'error' | 'history' }) {
  return (
    <div style={{ padding: tokens.space[4], textAlign: 'center' }}>
      <Icon name={icon} size={32} decorative />
      <p>{message}</p>
    </div>
  );
}

function buttonStyle(kind: 'primary' | 'secondary' | 'disabled') {
  return {
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background:
      kind === 'primary'
        ? tokens.color.accent.primary
        : kind === 'disabled'
          ? tokens.color.surface[2]
          : tokens.color.surface[1],
    color: kind === 'primary' ? tokens.color.accent.on : tokens.color.ink.primary,
    paddingInline: tokens.space[3],
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    cursor: kind === 'disabled' ? 'not-allowed' : 'pointer',
  };
}
